import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Plus, UserCheck, Search, Trash2, Edit3, X, Check } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { Skeleton } from '@/components/ui/skeleton.js';

interface Lead {
  id: string;
  contact_name: string;
  email: string;
  company?: string | null;
  service?: string | null;
  status: string;
  estimated_value?: number | null;
  source?: string | null;
  notes?: string | null;
}

export function LeadsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const canManage = isOwner || user?.role === 'manager';

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<string>('contact_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [newLead, setNewLead] = useState({
    contact_name: '',
    email: '',
    company: '',
    service: 'WordPress',
    status: 'new',
    estimated_value: '',
  });

  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [editForm, setEditForm] = useState({
    contact_name: '',
    email: '',
    company: '',
    service: 'WordPress',
    status: 'new',
    estimated_value: '',
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Delete Confirmation State
  const [deleteLeadTarget, setDeleteLeadTarget] = useState<{ id: string; name: string } | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!editingLead && !showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setEditingLead(null);
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingLead, showModal]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchLeads = async () => {
    try {
      const res = await api.get<{ data: Lead[] }>('/api/v1/leads');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setLeads(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleConvertLead = async (leadId: string) => {
    const idempotencyKey = `conv_${leadId}_${Date.now()}`;
    try {
      await api.post(`/api/v1/leads/${leadId}/convert`, {
        idempotency_key: idempotencyKey,
      });
      showToast('Lead converted into Client and Project successfully!');
      fetchLeads();
    } catch (e: any) {
      alert(e.message || 'Conversion failed');
    }
  };

  const handleCreateLead = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/leads', {
        ...newLead,
        estimated_value: newLead.estimated_value ? Number(newLead.estimated_value) : null,
      });
      setShowModal(false);
      setNewLead({ contact_name: '', email: '', company: '', service: 'WordPress', status: 'new', estimated_value: '' });
      showToast('Lead created successfully!');
      fetchLeads();
    } catch (e: any) {
      alert(e.message || 'Failed to create lead');
    }
  };

  const handleOpenEdit = (lead: Lead) => {
    setEditingLead(lead);
    setEditForm({
      contact_name: lead.contact_name || '',
      email: lead.email || '',
      company: lead.company || '',
      service: lead.service || 'WordPress',
      status: lead.status || 'new',
      estimated_value: lead.estimated_value ? String(lead.estimated_value) : '',
    });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;
    try {
      await api.patch(`/api/v1/leads/${editingLead.id}`, {
        ...editForm,
        estimated_value: editForm.estimated_value ? Number(editForm.estimated_value) : null,
      });
      setEditingLead(null);
      showToast('Lead updated successfully!');
      fetchLeads();
    } catch (e: any) {
      alert(e.message || 'Failed to update lead');
    }
  };

  const handleDeleteLead = (id: string, name: string) => {
    setDeleteLeadTarget({ id, name });
  };

  const confirmDeleteLead = async () => {
    if (!deleteLeadTarget) return;
    const { id, name } = deleteLeadTarget;
    try {
      await api.delete(`/api/v1/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setDeleteLeadTarget(null);
      showToast(`Lead "${name}" deleted.`);
      fetchLeads();
    } catch (e: any) {
      alert(e.message || 'Failed to delete lead');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    const matchesSearch = q
      ? (l.contact_name && l.contact_name.toLowerCase().includes(q)) ||
        (l.email && l.email.toLowerCase().includes(q)) ||
        (l.company && l.company.toLowerCase().includes(q)) ||
        (l.service && l.service.toLowerCase().includes(q))
      : true;
    const matchesStatus = statusFilter ? l.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Lead Pipeline</h1>
          <p className="helper-text mt-1">Track prospective agency projects and execute idempotent conversions.</p>
        </div>
        {canManage && (
          <Button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-1.5 font-semibold bg-brand-teal text-white hover:bg-brand-teal-action"
          >
            <Plus className="h-4 w-4" />
            <span>New Lead</span>
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-3 rounded-lg border border-slate-200">
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal_sent">Proposal Sent</option>
            <option value="negotiation">Negotiation</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <div className="flex items-center space-x-4 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort by:</span>
          <SortButton label="Contact" field="contact_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Company" field="company" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Status" field="status" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Service" field="service" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="divide-y divide-slate-100 bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2 flex-1">
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-16 rounded-full" />
                  <Skeleton className="h-4 w-20 rounded-full" />
                </div>
                <Skeleton className="h-3 w-56" />
              </div>
              <div className="flex items-center space-x-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-7 w-7 rounded-md" />
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : paginated.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-slate-500">No leads in pipeline matching criteria.</CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="divide-y divide-slate-100 bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
            {paginated.map((lead) => (
              <div key={lead.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-sm text-brand-navy">{lead.contact_name}</span>
                    <Badge variant={lead.status as any} className="text-[10px] capitalize">
                      {lead.status.replace('_', ' ')}
                    </Badge>
                    {lead.service && <Badge variant="secondary" className="text-[10px]">{lead.service}</Badge>}
                    {lead.estimated_value !== undefined && lead.estimated_value !== null && isOwner && (
                      <span className="text-[11px] font-mono font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                        ${Number(lead.estimated_value).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {lead.company && <span>{lead.company} • </span>}
                    <span>{lead.email}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {lead.status !== 'won' && canManage && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleConvertLead(lead.id)}
                      className="flex items-center space-x-1 text-xs border-brand-teal text-brand-teal hover:bg-teal-50 h-8"
                    >
                      <UserCheck className="h-3.5 w-3.5" />
                      <span>Convert to Project</span>
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleOpenEdit(lead)}
                      className="h-8 w-8 text-slate-400 hover:text-brand-navy"
                      title="Edit Lead"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  )}
                  {isOwner && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteLead(lead.id, lead.contact_name)}
                      className="h-8 w-8 text-slate-300 hover:text-red-600 hover:bg-red-50"
                      title="Delete Lead"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination
            totalItems={sorted.length}
            currentPage={validCurrentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Add Lead Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Add New Lead</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateLead} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Contact Name *</label>
                <Input
                  required
                  value={newLead.contact_name}
                  onChange={(e) => setNewLead({ ...newLead, contact_name: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Email Address *</label>
                <Input
                  required
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Company</label>
                <Input
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Service Category</label>
                <Input
                  value={newLead.service}
                  onChange={(e) => setNewLead({ ...newLead, service: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              {isOwner && (
                <div>
                  <label className="text-xs font-semibold text-slate-700">Estimated Value ($)</label>
                  <Input
                    type="number"
                    value={newLead.estimated_value}
                    onChange={(e) => setNewLead({ ...newLead, estimated_value: e.target.value })}
                    placeholder="e.g. 5000"
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Lead
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Lead Modal */}
      {editingLead && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setEditingLead(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Edit Lead</h2>
              <button onClick={() => setEditingLead(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Contact Name *</label>
                <Input
                  required
                  value={editForm.contact_name}
                  onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Email Address *</label>
                <Input
                  required
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Company</label>
                <Input
                  value={editForm.company}
                  onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-800 font-medium mt-1"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal_sent">Proposal Sent</option>
                  <option value="negotiation">Negotiation</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Service Category</label>
                <Input
                  value={editForm.service}
                  onChange={(e) => setEditForm({ ...editForm, service: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              {isOwner && (
                <div>
                  <label className="text-xs font-semibold text-slate-700">Estimated Value ($)</label>
                  <Input
                    type="number"
                    value={editForm.estimated_value}
                    onChange={(e) => setEditForm({ ...editForm, estimated_value: e.target.value })}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setEditingLead(null)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!deleteLeadTarget}
        itemType="Lead"
        itemName={deleteLeadTarget?.name}
        onClose={() => setDeleteLeadTarget(null)}
        onConfirm={confirmDeleteLead}
      />
    </div>
  );
}
